// =============================================================================
// ZARZĄDZANIE AGENTAMI RECEPTORA — pełny ekran, nie podgląd.
//
// Różnica wobec `SerweryDruku.tsx` obok jest celowa. Tamta karta jest PODGLĄDEM,
// bo kartoteka serwerów wydruku ma własny dział (`/admin/printing`) i dwa
// miejsca zapisu do jednej kartoteki znaczyłyby dwa źródła prawdy. Integracja
// z ERP własnego działu NIE MA i mieć nie powinna (RECEPTOR.md §3.1: wrzucenie
// jej do zakładki serwerów wydruku dałoby zakładkę o dwóch niepowiązanych
// treściach), więc jej jedynym miejscem zarządzania jest to.
//
// DWA KROKI, BO KOD ŻYJE 15 MINUT, A DROGA DO SERWEROWNI BYWA DŁUŻSZA.
// „Dodaj agenta" zakłada sam wpis — bez kodu nie daje on żadnego dostępu —
// a kod odnawia się przyciskiem na karcie, bez kasowania i zakładania wpisu
// od nowa. Ten sam układ, co przy serwerze wydruku i przy tablecie.
//
// DOWODEM SPAROWANIA JEST BAZA, NIE OPTYMIZM PANELU: okno odpytuje wiersz aż
// do `active`. Zamknięte wcześniej nie psuje niczego — kod żyje dalej.
//
// `token_jti` NIE JEST NAWET SELECT-OWANY (patrz `KOLUMNY` w `receptorCoreApi.ts`).
// Panel nie ma prawa być słabszy niż `agentPubliczny()` po stronie usługi.
// =============================================================================

import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  ModalFooter,
  Select,
  Spinner,
} from '@insolitum/ui';
import {
  Activity,
  Copy,
  Database,
  KeyRound,
  Plug,
  Plus,
  RefreshCw,
  Settings2,
  ShieldOff,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '../../../providers/AuthProvider';
import { OPIS_AGENTA, formatujCzasWzgledny, odliczanieDo } from '../printing/status';

import { WierszObiegu } from './WierszObiegu';
import {
  kluczZakladow,
  nazwyPoBledzie,
  prawaObiegu,
  type KursorObiegu,
  type NazwyZakladow,
  type ZakladObiegu,
} from './obiegDokumentow';
import {
  KOMUNIKAT_BRAK_RECEPTOR_CORE,
  OPIS_STATUSU_ZADANIA,
  RODZAJE_ZADAN_PANELU,
  RODZAJ_ODCZYTU_NASTAW,
  ReceptorCoreError,
  WERSJA_ZLECEN_BEZ_ZMIANY,
  etykietaPozycji,
  kodParowaniaAgenta,
  listaAgentow,
  listaKursorowObiegu,
  listaZadan,
  listaZakladow,
  liczbaZaleglychKontroli,
  odwolajAgenta,
  opisMeldunku,
  opisSynchronizacjiDokumentow,
  opisWersjiReceptora,
  odczytNastawAktualny,
  ostatniOdczytNastaw,
  receptorCoreDostepny,
  slownikiZWyniku,
  stanLicencjiReceptora,
  streszczenieWyniku,
  zadanieHarmonogramu,
  zadanieWToku,
  wersjaAgentaCoNajmniej,
  zadanieWgId,
  zakladyOrganizacji,
  zapiszNastawy,
  zarejestrujAgenta,
  zlecZadanie,
  zrodloZadania,
  type AgentReceptora,
  type KodParowaniaAgenta,
  type SlownikiNastaw,
  type StanLicencjiReceptora,
  type ZadanieAgenta,
} from './receptorCoreApi';
import { stanAgentaReceptora } from './stanAgentaReceptora';

/** Rytm odpytywania wiersza agenta w oknie parowania. */
export const POLL_PAROWANIA_MS = 5_000;
/**
 * Rytm zegara ekranu — i ODŚWIEŻANIA listy agentów (D6). Lista wczytana raz
 * zastygała, a zegar szedł dalej, więc żywy agent wychodził na ekranie
 * „offline” (zgłoszenie OGYY, 02.10.2026). Próg „offline” liczy się z okresu
 * pulsu agenta (`stanAgentaReceptora`, plan 59 K7): 90 s dla agenta co 5 s,
 * 3 min dla 0.7.x — a 90 s mieści zapis żywotności (≤ 30 s) i ten zegar.
 * Zostaje 30 s, bo dzieli go lista dokumentów (`ZakladkaDokumenty.tsx`).
 */
export const ZEGAR_MS = 30_000;

/**
 * Role, które zarządzają integracjami. Zbiór MUSI zgadzać się z migracją 729
 * (`receptor_agent_nowy_kod_parowania`) i z `ROLE_ADMINA_ORG` w receptor-core:
 * węższa lista w panelu dałaby przycisk wyłączony dla kogoś, komu baza i tak
 * pozwoli, a szersza — przycisk, który zawsze kończy się odmową.
 * `super_admin` jest obsłużony przez `hasRole` osobno.
 */
export const ROLE_ZARZADZAJACE = ['owner', 'admin', 'partner'] as const;

interface Props {
  /** Rytm polla — nadpisywany WYŁĄCZNIE w testach (determinizm bez fake timers). */
  pollMs?: number;
  /** Rytm zegara i odświeżania listy — nadpisywany WYŁĄCZNIE w testach. */
  zegarMs?: number;
}

/** Pierwsza wersja agenta, która pokazuje odmowę licencji wprost (faza `bez_licencji`). */
export const WERSJA_AGENTA_Z_LICENCJA = '0.4.0';

/**
 * Pierwsza wersja agenta, która sama instaluje nowe wydania (0.5.10). Starszą
 * (także 0.5.9) trzeba raz zaktualizować przyciskiem w oknie agenta; od 0.5.10
 * agent przy pełnych uprawnieniach administratora robi to sam (RECEPTOR.md
 * §3.5.2). Ten sam próg ma brama wydania (`PIERWSZA_Z_AUTOMATEM`
 * w `bramaPomiaruAutomatu.mjs`) — test pilnuje równości.
 */
export const WERSJA_AGENTA_Z_AUTOMATEM = '0.5.10';

/** `a < b` dla wersji `X.Y.Z` (agent nie publikuje przedrostków ani sufiksów). */
export function wersjaStarsza(a: string, b: string): boolean {
  const czesci = (v: string) => v.split('.').map((x) => Number.parseInt(x, 10) || 0);
  const [x, y] = [czesci(a), czesci(b)];
  for (let i = 0; i < 3; i += 1) {
    if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) < (y[i] ?? 0);
  }
  return false;
}

const dataPL = (iso: string | null): string =>
  iso ? iso.slice(0, 10).split('-').reverse().join('.') : '—';

/** „z N serwerA/serwerÓW" — dopełniacz po „z". */
const serwerowPoZ = (n: number): string => (n === 1 ? 'serwera' : 'serwerów');

/** „obejmuje N serwer/serwery/serwerów" — biernik, ta sama odmiana co w SQL. */
const serwerowBiernik = (n: number): string =>
  n === 1
    ? 'serwer'
    : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)
      ? 'serwery'
      : 'serwerów';

/**
 * Stan licencji w jednym zdaniu — albo `null`, gdy licencja nie odmawia
 * i nie ma czego mówić poza liczbą (wtedy `zdanieLicencji` zwraca opis).
 * Czysta funkcja: test mierzy zdania bez renderowania ekranu.
 */
export function zdanieLicencji(l: StanLicencjiReceptora): {
  ton: 'ok' | 'uwaga' | 'blad';
  tekst: string;
} {
  if (!l.ok && l.kod === 'license_missing') {
    return {
      ton: 'blad',
      tekst: 'Brak licencji agenta — skontaktuj się z opiekunem, aby ją wykupić.',
    };
  }
  if (!l.ok) {
    return { ton: 'blad', tekst: l.komunikat ?? 'Licencja agenta nie obowiązuje.' };
  }
  if (l.laska) {
    return {
      ton: 'uwaga',
      tekst: `Licencja wygasła ${dataPL(l.wazna_do)} — agenci pracują jeszcze do ${dataPL(l.laska_do)}. Skontaktuj się z opiekunem.`,
    };
  }
  const ile = l.bez_limitu
    ? `bez limitu serwerów (aktywnych agentów: ${l.serwery_zajete})`
    : `${l.serwery_zajete} z ${l.serwery_limit ?? 0} ${serwerowPoZ(l.serwery_limit ?? 0)}`;
  const okres =
    l.okres === 'monthly'
      ? `miesięcznie do ${dataPL(l.wazna_do)}`
      : l.okres === 'yearly'
        ? `rocznie do ${dataPL(l.wazna_do)}`
        : l.okres === 'on_premise'
          ? 'wykup — bez daty końca'
          : 'nadanie — bez daty końca';
  return { ton: 'ok', tekst: `Licencja agenta: ${ile} · ${okres}` };
}

/** Czy licencja pozwala dodać KOLEJNY serwer — `null` = nie wiadomo (nie blokujemy, decyduje baza). */
export function miejsceNaSerwer(l: StanLicencjiReceptora | 'blad' | null): {
  wolno: boolean;
  powod: string | null;
} {
  if (l === null || l === 'blad') return { wolno: true, powod: null };
  if (!l.ok) return { wolno: false, powod: zdanieLicencji(l).tekst };
  if (!l.bez_limitu && l.serwery_limit !== null && l.serwery_zajete >= l.serwery_limit) {
    return {
      wolno: false,
      powod: `Licencja obejmuje ${l.serwery_limit} ${serwerowBiernik(l.serwery_limit)} i wszystkie miejsca są zajęte — sparuj ponownie istniejący wpis, odwołaj nieużywanego agenta albo dokup serwer u opiekuna.`,
    };
  }
  return { wolno: true, powod: null };
}

export function AgenciReceptora({ pollMs = POLL_PAROWANIA_MS, zegarMs = ZEGAR_MS }: Props) {
  const { currentOrganization, hasRole, user } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const admin = hasRole([...ROLE_ZARZADZAJACE]);
  const zapisMozliwy = receptorCoreDostepny();
  // Obieg dokumentów ma WĘŻSZE prawa niż reszta ekranu (bez partnera, D53-61) i
  // dwa dodatkowe: podgląd kierownika oraz przepięcie wyłącznie dla super-admina.
  // Dlatego nie `admin` powyżej, tylko osobna lista z `obiegDokumentow.ts`.
  const prawa = prawaObiegu({
    rola: currentOrganization?.role,
    statusCzlonkostwa: currentOrganization?.status,
    superAdmin: user?.is_super_admin === true,
  });
  const podgladObiegu = prawa.podglad;

  const [agenci, setAgenci] = useState<AgentReceptora[]>([]);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);
  // TRZY STANY: `null` = jeszcze nie wiadomo, `'blad'` = nie udało się
  // sprawdzić (to NIE jest „brak licencji"), obiekt = werdykt z bazy.
  const [licencja, setLicencja] = useState<StanLicencjiReceptora | 'blad' | null>(null);

  // `false` = zamknięte, `null` = krok „nazwa", wiersz = od razu krok „kod".
  const [parowanie, setParowanie] = useState<AgentReceptora | null | false>(false);
  const [doOdwolania, setDoOdwolania] = useState<AgentReceptora | null>(null);
  const [nastawyDla, setNastawyDla] = useState<AgentReceptora | null>(null);
  const [odwoluje, setOdwoluje] = useState(false);
  const [bladAkcji, setBladAkcji] = useState<string | null>(null);

  const [teraz, setTeraz] = useState(() => Date.now());

  const odswiez = useCallback(async () => {
    if (!orgId) return;
    try {
      // Lista podmieniana WYŁĄCZNIE przy sukcesie — chwilowy błąd odczytu
      // nie czyści ekranu.
      setAgenci(await listaAgentow(orgId));
      setBlad(null);
    } catch (err) {
      setBlad(err instanceof ReceptorCoreError ? err.message : 'Nie udało się wczytać agentów');
    } finally {
      setLadowanie(false);
    }
    // Licencja osobno: jej brak nie ma prawa zasłonić listy agentów.
    try {
      setLicencja((await stanLicencjiReceptora(orgId)) ?? 'blad');
    } catch {
      setLicencja('blad');
    }
  }, [orgId]);

  const miejsce = miejsceNaSerwer(licencja);
  const wLicencji = (agentId: string): boolean | null => {
    if (licencja === null || licencja === 'blad' || !Array.isArray(licencja.agenci)) return null;
    const wpis = licencja.agenci.find((a) => a.id === agentId);
    return wpis ? wpis.w_licencji : null;
  };

  useEffect(() => {
    void odswiez();
  }, [odswiez]);

  // ── Kolejka zadań (migracja 732) ─────────────────────────────────────────
  //
  // ODCZYT wprost z bazy (`_tenant_read`), ZLECENIE przez usługę — ten sam
  // podział, co dla kartoteki agentów. Lista jest odpytywana WYŁĄCZNIE, dopóki
  // jakieś zadanie jest w toku: stała pętla odświeżania na ekranie, na którym
  // nic się nie dzieje, to ruch do bazy bez jednej nowej informacji.
  const [zadania, setZadania] = useState<ZadanieAgenta[]>([]);
  const [zlecam, setZlecam] = useState<string | null>(null);
  /** Błąd ZLECENIA — zostaje do następnego zlecenia, bo dotyczy decyzji człowieka. */
  const [bladZadania, setBladZadania] = useState<string | null>(null);
  /**
   * Błąd ODCZYTU listy — osobno, bo gaśnie przy pierwszym udanym odczycie.
   * Od D6 lista odświeża się w każdym tiku zegara; jeden nieudany tik
   * (wybudzony laptop, timeout PostgREST) zostawiał na ekranie czerwony
   * komunikat na stałe, obok listy, która już się odświeżała.
   */
  const [bladOdczytuZadan, setBladOdczytuZadan] = useState<string | null>(null);

  const odswiezZadania = useCallback(async () => {
    if (!orgId) return;
    try {
      // Filtr w zapytaniu (`FILTR_ZADAN_BEZ_HARMONOGRAMU`) jest pierwszą linią;
      // ta jest drugą. Zadanie harmonogramu w toku włączyłoby szybkie odpytywanie
      // (`cosWToku` niżej) na stałe — harmonogram zakłada je co kilka minut.
      setZadania((await listaZadan(orgId)).filter((z) => !zadanieHarmonogramu(z)));
      setBladOdczytuZadan(null);
    } catch (err) {
      setBladOdczytuZadan(
        err instanceof ReceptorCoreError ? err.message : 'Nie udało się wczytać zadań'
      );
    }
  }, [orgId]);

  useEffect(() => {
    void odswiezZadania();
  }, [odswiezZadania]);

  // ── Obieg dokumentów (UD-11a) ────────────────────────────────────────────
  //
  // ODCZYT wprost z bazy, kluczem użytkownika (`receptor_kursory_tenant_read`,
  // `commerce_documents_tenant_read`: owner, admin, manager, super-admin). Panel
  // pyta TYLKO, gdy ma prawo podglądu — partner dostałby pustą listę, a pusta
  // lista to „obieg nieaktywny”, czyli nieprawda.
  //
  // Trzy stany każdej danych, nie dwa: `null` = jeszcze nie wiadomo, `'blad'` =
  // odczyt się nie udał (to NIE jest „brak kursora”), wartość = werdykt z bazy.
  // Tik zegara, który chwilowo nie dostał odpowiedzi, NIE zastępuje dobrej listy
  // błędem — jak lista agentów wyżej.
  const [kursory, setKursory] = useState<KursorObiegu[] | 'blad' | null>(null);
  // Dwa odczyty zakładów (P2): `zaklady` = lista do NOWEGO włączenia (tylko aktywne),
  // `zakladyKursorow` = nazwy i status zakładów już związanych z kursorami (wszystkie,
  // z kluczem zbioru id). Nazwa kursora z samej listy do włączenia zamieniłaby
  // zakład usunięty na ekranie Lokalizacje w „zakład spoza listy”.
  const [zaklady, setZaklady] = useState<ZakladObiegu[] | 'blad' | null>(null);
  const [zakladyKursorow, setZakladyKursorow] = useState<NazwyZakladow>(null);
  const [zalegle, setZalegle] = useState<number | 'blad' | null>(null);
  // Odpowiedź dla poprzedniej organizacji nie ma prawa wylądować na ekranie nowej.
  const orgAktualna = useRef(orgId);
  useEffect(() => {
    orgAktualna.current = orgId;
    setKursory(null);
    setZaklady(null);
    setZakladyKursorow(null);
    setZalegle(null);
  }, [orgId]);

  const odswiezObieg = useCallback(async () => {
    if (!orgId || !podgladObiegu) return;
    const org = orgId;
    const biezaca = () => orgAktualna.current === org;
    let lista: KursorObiegu[];
    try {
      lista = await listaKursorowObiegu(org);
    } catch {
      if (biezaca()) setKursory((poprzednie) => (Array.isArray(poprzednie) ? poprzednie : 'blad'));
      return;
    }
    if (!biezaca()) return;
    setKursory(lista);

    // Nazwy zakładów kursorów — jedno zapytanie po kluczu głównym na tik, tylko
    // gdy kursor istnieje (puste id → bez zapytania). Błąd zachowuje poprzednią
    // listę wyłącznie wtedy, gdy była wczytana dla tych samych id (`nazwyPoBledzie`).
    const ids = lista.map((k) => k.zaklad_id);
    try {
      const nazwy = await zakladyOrganizacji(org, { ids });
      if (biezaca()) setZakladyKursorow({ klucz: kluczZakladow(ids), lista: nazwy });
    } catch {
      if (biezaca()) setZakladyKursorow((poprzednie) => nazwyPoBledzie(poprzednie, ids));
    }

    // Zaległość kontroli ma sens tylko przy obiegu, który działa — zliczanie
    // dokumentów dla organizacji bez obiegu byłoby zapytaniem bez odbiorcy.
    if (!lista.some((k) => k.stan === 'aktywny')) {
      setZalegle(null);
      return;
    }
    try {
      const n = await liczbaZaleglychKontroli(org);
      if (biezaca()) setZalegle(n);
    } catch {
      if (biezaca())
        setZalegle((poprzednie) => (typeof poprzednie === 'number' ? poprzednie : 'blad'));
    }
  }, [orgId, podgladObiegu]);

  const odswiezZaklady = useCallback(async () => {
    if (!orgId || !podgladObiegu) return;
    const org = orgId;
    try {
      const lista = await listaZakladow(org);
      if (orgAktualna.current === org) setZaklady(lista);
    } catch {
      if (orgAktualna.current === org) {
        setZaklady((poprzednie) => (Array.isArray(poprzednie) ? poprzednie : 'blad'));
      }
    }
  }, [orgId, podgladObiegu]);

  useEffect(() => {
    void odswiezObieg();
    void odswiezZaklady();
  }, [odswiezObieg, odswiezZaklady]);

  // Zegar ekranu i ODŚWIEŻENIE danych w tym samym tiku (D6). Sam zegar bez
  // odświeżenia porównywał zamrożone `last_seen_at` z bieżącą chwilą
  // i pokazywał żywego agenta jako „offline”.
  useEffect(() => {
    const id = setInterval(() => {
      setTeraz(Date.now());
      void odswiez();
      void odswiezZadania();
      void odswiezObieg();
    }, zegarMs);
    return () => clearInterval(id);
  }, [odswiez, odswiezZadania, odswiezObieg, zegarMs]);

  const cosWToku = zadania.some(zadanieWToku);
  useEffect(() => {
    if (!cosWToku) return;
    const id = setInterval(() => void odswiezZadania(), pollMs);
    return () => clearInterval(id);
  }, [cosWToku, odswiezZadania, pollMs]);

  const zlec = async (agent: AgentReceptora, rodzaj: string) => {
    if (!orgId) return;
    const klucz = `${agent.id}:${rodzaj}`;
    setZlecam(klucz);
    setBladZadania(null);
    try {
      await zlecZadanie(orgId, { rodzaj, agent_id: agent.id });
      await Promise.all([odswiezZadania(), odswiez()]);
    } catch (err) {
      setBladZadania(
        err instanceof ReceptorCoreError ? err.message : 'Nie udało się zlecić zadania'
      );
    } finally {
      setZlecam(null);
    }
  };

  const nazwaAgenta = (agentId: string | null): string =>
    agentId === null
      ? 'dowolny agent'
      : (agenci.find((a) => a.id === agentId)?.name ?? 'agent usunięty');

  const odwolaj = async () => {
    if (!orgId || !doOdwolania) return;
    setOdwoluje(true);
    setBladAkcji(null);
    try {
      await odwolajAgenta(orgId, doOdwolania.id);
      setDoOdwolania(null);
      await odswiez();
    } catch (err) {
      setBladAkcji(err instanceof ReceptorCoreError ? err.message : 'Nie udało się odwołać agenta');
    } finally {
      setOdwoluje(false);
    }
  };

  return (
    <div className="card p-4 mt-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-aurora-text flex items-center gap-2">
            <Plug className="w-4 h-4 text-aurora-brand" />
            Zarejestrowane integracje Receptora
          </h2>
          <p className="mt-1 text-xs text-aurora-muted">
            Jeden agent na serwer z Receptorem. Wpis bez kodu parowania nie daje żadnego dostępu.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              void odswiez();
              // Lista zakładów do okna „Włącz obieg” wczytuje się raz, przy wejściu na ekran;
              // bez tego zakład dodany w drugiej karcie byłby widoczny dopiero po przeładowaniu.
              void odswiezZaklady();
            }}
            className="iconbtn"
            title="Odśwież"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          {admin && (
            <Button
              onClick={() => setParowanie(null)}
              disabled={!zapisMozliwy || !miejsce.wolno}
              title={!zapisMozliwy ? KOMUNIKAT_BRAK_RECEPTOR_CORE : (miejsce.powod ?? undefined)}
              size="sm"
            >
              <Plus className="w-4 h-4" />
              Dodaj agenta
            </Button>
          )}
        </div>
      </div>

      {licencja && licencja !== 'blad' && (
        <PasekLicencji
          licencja={licencja}
          // Super-admin (także w wejściu serwisowym) dostaje skrót do jedynego
          // miejsca, w którym licencję agenta się ustawia — klient widzi samo
          // zdanie „skontaktuj się z opiekunem", bo opiekunem jest ten ekran.
          licencjaOperatora={
            user?.is_super_admin === true && orgId
              ? `/super-admin/klienci-receptora?zakladka=licencja&organizacja=${orgId}`
              : null
          }
        />
      )}
      {licencja === 'blad' && (
        <Alert variant="info" className="mb-4">
          Nie udało się sprawdzić licencji agenta — spróbuj odświeżyć. Agent sam zgłosi brak
          licencji przy pracy.
        </Alert>
      )}
      {admin &&
        !miejsce.wolno &&
        miejsce.powod &&
        licencja !== null &&
        licencja !== 'blad' &&
        licencja.ok && <p className="mb-4 text-xs text-aurora-muted">{miejsce.powod}</p>}

      {blad && (
        <Alert variant="error" className="mb-4">
          {blad}
        </Alert>
      )}
      {bladAkcji && (
        <Alert variant="error" className="mb-4">
          {bladAkcji}
        </Alert>
      )}
      {admin && !zapisMozliwy && (
        <Alert variant="info" className="mb-4">
          {KOMUNIKAT_BRAK_RECEPTOR_CORE} — lista poniżej jest aktualna, ale dodawanie i odwoływanie
          agentów będzie możliwe po jego wdrożeniu.
        </Alert>
      )}

      {ladowanie ? (
        // Szkielet w geometrii docelowej: wycentrowany spinner przerzucałby
        // układ przy każdym odświeżeniu.
        <div className="space-y-2">
          <div className="skel h-16" />
          <div className="skel h-16" />
        </div>
      ) : agenci.length === 0 ? (
        <EmptyState
          variant="card"
          icon={<Plug className="w-8 h-8" />}
          title="Brak integracji Receptora"
          description="Zainstaluj agenta na serwerze z Receptorem (instalator powyżej), dodaj tu wpis i wygeneruj dla niego sześciocyfrowy kod parowania."
          action={
            admin ? (
              <Button
                onClick={() => setParowanie(null)}
                disabled={!zapisMozliwy || !miejsce.wolno}
                title={miejsce.powod ?? undefined}
                size="sm"
              >
                <Plus className="w-4 h-4" />
                Dodaj agenta
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-2">
          {agenci.map((agent) => {
            // Próg z okresu pulsu agenta (plan 59, K7), nie stałe 3 min druku.
            const stan = stanAgentaReceptora(agent, teraz);
            const opis = OPIS_AGENTA[stan];
            return (
              <div key={agent.id} className="p-3 rounded-ids-md border border-aurora-border">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-aurora-text truncate">{agent.name}</p>
                    <p className="text-xs text-aurora-subtle mono truncate">
                      {agent.hostname ?? 'host nieznany'}
                      {agent.platform ? ` · ${agent.platform}` : ''}
                      {agent.app_version ? ` · v${agent.app_version}` : ''}
                    </p>
                    {agent.status === 'active' &&
                      agent.app_version &&
                      wersjaStarsza(agent.app_version, WERSJA_AGENTA_Z_LICENCJA) && (
                        <p className="text-xs text-aurora-muted">
                          Zaktualizuj agenta do {WERSJA_AGENTA_Z_LICENCJA}, aby na serwerze widział
                          stan licencji.
                        </p>
                      )}
                    {/* Panel zna tylko app_version: agenta „tylko ręcznie” albo po trzech
                        nieudanych próbach nie odróżni od czekającego na wydanie — rejestr
                        długu: autoaktualizacja_agenta_bez_meldunku_w_universe. */}
                    {agent.status === 'active' &&
                      agent.app_version &&
                      wersjaStarsza(agent.app_version, WERSJA_AGENTA_Z_AUTOMATEM) && (
                        <p className="text-xs text-aurora-muted">
                          Ta wersja agenta nie instaluje aktualizacji sama. Na serwerze kliknij
                          „Zainstaluj i zrestartuj” w oknie agenta, gdy pojawi się nowa wersja.
                          Otworzy się okno instalatora (na koncie z Kontrolą konta użytkownika
                          najpierw pytanie o zgodę): poczekaj na ostatnią stronę i kliknij „Zakończ”
                          z zaznaczonym polem uruchomienia agenta — dopiero wtedy agent wstaje, więc
                          nie rozłączaj sesji przed tym krokiem. Od wersji{' '}
                          {WERSJA_AGENTA_Z_AUTOMATEM} przy pełnych uprawnieniach administratora
                          agent robi to sam.
                        </p>
                      )}
                    {agent.status !== 'revoked' && <MeldunekKonfiguracji agent={agent} />}
                    {agent.status !== 'revoked' && <WersjaReceptora agent={agent} teraz={teraz} />}
                    {agent.status !== 'revoked' && (
                      <SynchronizacjaDokumentow agent={agent} teraz={teraz} />
                    )}
                    {agent.status !== 'revoked' && orgId && (
                      <WierszObiegu
                        organizationId={orgId}
                        agent={agent}
                        agenci={agenci}
                        prawa={prawa}
                        kursory={kursory}
                        zaklady={zaklady}
                        zakladyKursorow={zakladyKursorow}
                        ponowZaklady={() => void odswiezZaklady()}
                        zalegle={zalegle}
                        teraz={teraz}
                        zapisMozliwy={zapisMozliwy}
                        onZmiana={() => void odswiezObieg()}
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-aurora-muted">
                      {formatujCzasWzgledny(agent.last_seen_at, teraz)}
                    </span>
                    <Badge variant={opis.wariant}>
                      {opis.symbol} {opis.etykieta}
                    </Badge>
                    {agent.status === 'active' && wLicencji(agent.id) === false && (
                      // Osobna plakietka Receptora — `stanAgentaReceptora`
                      // nie zna licencji i nie ma jej poznawać.
                      <Badge variant="warning">Poza licencją — nie pobiera pracy</Badge>
                    )}
                    {admin && agent.status !== 'revoked' && (
                      <div className="flex items-center gap-1">
                        {agent.status === 'active' &&
                          Object.entries(RODZAJE_ZADAN_PANELU)
                            // Odczyt magazynów i zmian ma swoje miejsce w oknie
                            // nastaw — tam, gdzie jego wynik jest potrzebny.
                            .filter(([rodzaj, o]) => o.zPanelu && rodzaj !== RODZAJ_ODCZYTU_NASTAW)
                            .map(([rodzaj, opisRodzaju]) => (
                              <Button
                                key={rodzaj}
                                variant="secondary"
                                size="sm"
                                onClick={() => void zlec(agent, rodzaj)}
                                disabled={!zapisMozliwy || zlecam !== null}
                                title={
                                  zapisMozliwy ? opisRodzaju.opis : KOMUNIKAT_BRAK_RECEPTOR_CORE
                                }
                              >
                                {zlecam === `${agent.id}:${rodzaj}` ? (
                                  <Spinner size="sm" />
                                ) : rodzaj === 'receptor.ping' ? (
                                  <Activity className="w-4 h-4" />
                                ) : (
                                  <Database className="w-4 h-4" />
                                )}
                                {opisRodzaju.etykieta}
                              </Button>
                            ))}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setNastawyDla(agent)}
                          disabled={!zapisMozliwy}
                          title={
                            zapisMozliwy
                              ? 'Magazyny i zmiany dla zleceń z Mandatum'
                              : KOMUNIKAT_BRAK_RECEPTOR_CORE
                          }
                        >
                          <Settings2 className="w-4 h-4" />
                          Nastawy
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setParowanie(agent)}
                          disabled={!zapisMozliwy}
                          title={
                            zapisMozliwy ? 'Wystaw kod parowania' : KOMUNIKAT_BRAK_RECEPTOR_CORE
                          }
                        >
                          <KeyRound className="w-4 h-4" />
                          Kod parowania
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDoOdwolania(agent)}
                          disabled={!zapisMozliwy}
                          title={zapisMozliwy ? 'Odwołaj agenta' : KOMUNIKAT_BRAK_RECEPTOR_CORE}
                        >
                          <ShieldOff className="w-4 h-4" />
                          Odwołaj
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {agent.status !== 'revoked' && orgId && (
                  <PodsumowanieNastaw organizationId={orgId} agent={agent} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {(zadania.length > 0 || bladZadania || bladOdczytuZadan) && (
        <div className="mt-6">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-aurora-muted mb-2">
            Ostatnie zadania
          </h3>
          {bladZadania && (
            <Alert variant="error" className="mb-2">
              {bladZadania}
            </Alert>
          )}
          {bladOdczytuZadan && (
            <Alert variant="error" className="mb-2">
              {bladOdczytuZadan}
            </Alert>
          )}
          <ul className="space-y-1" aria-label="Ostatnie zadania agentów Receptora">
            {zadania.map((z) => {
              const opisStatusu = OPIS_STATUSU_ZADANIA[z.status];
              const streszczenie = streszczenieWyniku(z);
              return (
                <li
                  key={z.id}
                  className="flex items-center justify-between gap-3 flex-wrap px-3 py-2 rounded-ids-md bg-aurora-surface-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-aurora-text">
                      {RODZAJE_ZADAN_PANELU[z.rodzaj]?.etykieta ?? z.rodzaj}
                      {zrodloZadania(z) && (
                        <span className="text-aurora-muted"> · {zrodloZadania(z)}</span>
                      )}
                      <span className="text-aurora-muted"> · {nazwaAgenta(z.agent_id)}</span>
                    </p>
                    {streszczenie && (
                      <p className="text-xs text-aurora-subtle truncate" title={streszczenie}>
                        {streszczenie}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-aurora-muted">
                      {formatujCzasWzgledny(z.zakonczone_at ?? z.podjete_at ?? z.created_at, teraz)}
                    </span>
                    <Badge variant={opisStatusu.wariant}>
                      {opisStatusu.symbol} {opisStatusu.etykieta}
                    </Badge>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {nastawyDla && orgId && (
        <OknoNastaw
          organizationId={orgId}
          agent={nastawyDla}
          pollMs={pollMs}
          onZamknij={() => {
            setNastawyDla(null);
            void odswiez();
            void odswiezZadania();
          }}
        />
      )}

      {parowanie !== false && orgId && (
        <OknoParowania
          organizationId={orgId}
          agent={parowanie}
          pollMs={pollMs}
          onZamknij={() => {
            setParowanie(false);
            void odswiez();
          }}
        />
      )}

      <Modal
        open={doOdwolania !== null}
        onClose={() => setDoOdwolania(null)}
        title="Odwołać agenta?"
        description={
          doOdwolania
            ? `Klucz dostępu agenta „${doOdwolania.name}” przestanie działać natychmiast. Integracja z Receptorem zatrzyma się do czasu ponownego sparowania.`
            : undefined
        }
        footer={
          <ModalFooter>
            <Button variant="ghost" onClick={() => setDoOdwolania(null)} disabled={odwoluje}>
              Anuluj
            </Button>
            <Button variant="danger" onClick={() => void odwolaj()} disabled={odwoluje}>
              {odwoluje ? <Spinner size="sm" /> : <ShieldOff className="w-4 h-4" />}
              Odwołaj
            </Button>
          </ModalFooter>
        }
      >
        <p className="text-sm text-aurora-muted">
          Wpis zostaje na liście — możesz sparować go ponownie nowym kodem, gdy serwer wróci.
        </p>
      </Modal>
    </div>
  );
}

// ── Okno parowania: krok „nazwa" → krok „kod" ───────────────────────────────

function OknoParowania({
  organizationId,
  agent,
  pollMs,
  onZamknij,
}: {
  organizationId: string;
  agent: AgentReceptora | null;
  pollMs: number;
  onZamknij: () => void;
}) {
  const [nazwa, setNazwa] = useState('');
  const [pracuje, setPracuje] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const [kod, setKod] = useState<KodParowaniaAgenta | null>(null);
  const [sparowany, setSparowany] = useState(false);
  const [teraz, setTeraz] = useState(() => Date.now());

  // Odliczanie ważności kodu — sekundowe, bo kod żyje kwadrans.
  useEffect(() => {
    if (!kod) return;
    const id = setInterval(() => setTeraz(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [kod]);

  // Dowodem sparowania jest BAZA: odpytujemy wiersz aż do `active`.
  useEffect(() => {
    if (!kod || sparowany) return;
    const id = setInterval(() => {
      void (async () => {
        try {
          const lista = await listaAgentow(organizationId);
          const wiersz = lista.find((a) => a.id === kod.agent_id);
          if (wiersz?.status === 'active') setSparowany(true);
        } catch {
          // Chwilowy błąd odczytu nie ma prawa zamknąć okna ani skasować kodu —
          // kod żyje dalej, a następne odpytanie spróbuje ponownie.
        }
      })();
    }, pollMs);
    return () => clearInterval(id);
  }, [kod, sparowany, organizationId, pollMs]);

  const wystaw = useCallback(async (agentId: string) => {
    setPracuje(true);
    setBlad(null);
    try {
      setKod(await kodParowaniaAgenta(agentId));
    } catch (err) {
      setBlad(err instanceof ReceptorCoreError ? err.message : 'Nie udało się wystawić kodu');
    } finally {
      setPracuje(false);
    }
  }, []);

  const dodajIWystaw = async () => {
    setPracuje(true);
    setBlad(null);
    try {
      const nowy = await zarejestrujAgenta(organizationId, nazwa);
      await wystaw(nowy.id);
    } catch (err) {
      setBlad(err instanceof ReceptorCoreError ? err.message : 'Nie udało się dodać agenta');
      setPracuje(false);
    }
  };

  // Wejście „odnów kod dla istniejącego wpisu" pomija krok nazwy.
  //
  // Wartownik `useRef`, a NIE warunek na stanie: `pracuje`/`blad`/`kod` zmieniają
  // się w trakcie wystawiania, więc efekt zależny od nich potrafiłby wystawić
  // DRUGI kod, a każde wystawienie gasi poprzedni (`UPDATE … SET expires_at =
  // NOW()` w migracji 729). Człowiek przy serwerze wpisywałby wtedy cyfry
  // unieważnione sekundę wcześniej.
  const wystawionoDla = useRef<string | null>(null);
  useEffect(() => {
    if (!agent || wystawionoDla.current === agent.id) return;
    wystawionoDla.current = agent.id;
    void wystaw(agent.id);
  }, [agent, wystaw]);

  const zostalo = kod ? odliczanieDo(kod.expires_at, teraz) : null;

  return (
    <Modal
      open
      onClose={onZamknij}
      title={kod ? 'Kod parowania' : 'Nowy agent Receptora'}
      description={
        kod
          ? 'Wpisz ten kod w oknie agenta na serwerze z Receptorem.'
          : 'Nazwa pomaga odróżnić serwery, gdy jest ich kilka — np. „Receptor — zakład Rdestowa”.'
      }
      footer={
        <ModalFooter>
          <Button variant="ghost" onClick={onZamknij}>
            {sparowany ? 'Gotowe' : 'Zamknij'}
          </Button>
          {!kod && (
            <Button onClick={() => void dodajIWystaw()} disabled={pracuje || nazwa.trim() === ''}>
              {pracuje ? <Spinner size="sm" /> : <KeyRound className="w-4 h-4" />}
              Dodaj i wystaw kod
            </Button>
          )}
        </ModalFooter>
      }
    >
      {blad && (
        <Alert variant="error" className="mb-4">
          {blad}
        </Alert>
      )}

      {!kod ? (
        <label className="block">
          <span className="block text-xs font-medium text-aurora-muted mb-1">Nazwa agenta</span>
          <Input
            value={nazwa}
            onChange={(e) => setNazwa(e.target.value)}
            placeholder="Receptor — zakład Rdestowa"
            autoFocus
          />
        </label>
      ) : (
        <div className="space-y-4">
          <div className="text-center">
            <p className="mono text-4xl font-semibold tracking-[0.3em] text-aurora-text">
              {kod.code}
            </p>
            <button
              onClick={() => void navigator.clipboard?.writeText(kod.code)}
              className="btn btn--tonal btn--sm mt-3"
            >
              <Copy className="w-4 h-4" />
              Kopiuj
            </button>
          </div>

          {/* Trzy stany, nie dwa: czekam / sparowano / kod wygasł. „Kod wygasł"
              jest stanem NAZWANYM, nie brakiem odpowiedzi — inaczej człowiek
              przy serwerze wpisywałby martwe cyfry i nie wiedział dlaczego. */}
          {sparowany ? (
            <Alert variant="success">
              Agent „{kod.agent_name}” został sparowany. Możesz zamknąć to okno.
            </Alert>
          ) : zostalo ? (
            <Alert variant="info">
              Kod jest ważny jeszcze <span className="mono">{zostalo}</span>. Okno samo wykryje
              sparowanie — nie trzeba go pilnować.
            </Alert>
          ) : (
            <Alert variant="warning">
              Kod wygasł. Zamknij okno i wystaw nowy przyciskiem „Kod parowania”.
            </Alert>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Meldunek konfiguracji (agent 0.3.2, D4) ─────────────────────────────────

/**
 * Którą bazę i które API obsługuje agent — z jego meldunku w pulsie. Stany
 * są nazwane (`opisMeldunku`): „nie podaje”, „odrzucony”, „brak konfiguracji”
 * i konfiguracja. Nazwa hosta maszyny (wyżej) NIE jest serwerem bazy — na
 * serwerze z kilkoma kompletami Receptora to dwie różne rzeczy.
 */
function MeldunekKonfiguracji({ agent }: { agent: AgentReceptora }) {
  const m = opisMeldunku(agent);
  return (
    <p
      className={`text-xs mono truncate ${
        m.stan === 'odrzucony' || m.niepotwierdzona ? 'text-aurora-warning' : 'text-aurora-subtle'
      }`}
      data-testid={`meldunek-${agent.id}`}
      title={m.tekst}
    >
      {m.tekst}
      {m.niepotwierdzona &&
        ' — konfiguracja sprzed 0.3.2 niepotwierdzona: agent nie pobiera zadań. Jeśli ta maszyna nie była sparowana z innym wpisem agenta, potwierdzi ją sam przy najbliższym meldunku; jeśli była — potwierdza ją człowiek przy serwerze'}
    </p>
  );
}

// ── Wersja Receptora (agent 0.5.2, migracja 760) ────────────────────────────

/**
 * Jaki Receptor stoi na serwerze — z odczytu agenta (`opisWersjiReceptora`).
 * Cztery stany, każdy nazwany: „Receptor 3.0.5.0 · produkcja: …”, „Receptor
 * sprzed 3.0.5 — schemat: …”, „Wersji Receptora nie podaje (powód)” i „agent
 * nie podaje” (NULL, wersja agenta sprzed 0.5.2). Ostatni nie ma prawa
 * wyglądać jak trzeci: tamten jest usterką do zbadania, ten — brakiem
 * aktualizacji agenta.
 */
function WersjaReceptora({ agent, teraz }: { agent: AgentReceptora; teraz: number }) {
  const w = opisWersjiReceptora(agent, teraz);
  return (
    <p
      className={`text-xs mono truncate ${
        w.uwaga
          ? 'text-aurora-warning'
          : w.stan === 'agent_nie_podaje'
            ? 'text-aurora-muted'
            : 'text-aurora-subtle'
      }`}
      data-testid={`wersja-receptora-${agent.id}`}
      data-stan={w.stan}
      title={w.szczegol ? `${w.tekst}\n${w.szczegol}` : w.tekst}
    >
      {w.tekst}
    </p>
  );
}

// ── Synchronizacja dokumentów (UD-4, migracja 785) ──────────────────────────

/**
 * Czy Receptor na tym serwerze ma trasę synchronizacji dokumentów — z sondy
 * agenta (`opisSynchronizacjiDokumentow`). Zieleń WYŁĄCZNIE przy zmierzonym
 * `dok-mag/1`; „agent nie podaje” jest wyciszone (brak aktualizacji agenta),
 * a „brak trasy” i „nie sprawdzono” wymagają działania.
 */
function SynchronizacjaDokumentow({ agent, teraz }: { agent: AgentReceptora; teraz: number }) {
  const s = opisSynchronizacjiDokumentow(agent, teraz);
  return (
    <p
      className={`text-xs truncate ${
        s.dobrze ? 'text-aurora-success' : s.uwaga ? 'text-aurora-warning' : 'text-aurora-muted'
      }`}
      data-testid={`synchronizacja-dokumentow-${agent.id}`}
      data-stan={s.stan}
      title={s.tekst}
    >
      {s.tekst}
    </p>
  );
}

// ── Nastawy zlecenia (migracja 733) ─────────────────────────────────────────

/**
 * Jedna linia pod agentem: komplet nastaw albo NAZWANY brak. Brak nie jest
 * neutralny — zlecenie z Mandatum do takiego agenta skończy się odmową
 * `nastawy_missing`, więc linia mówi to wprost.
 */
function PodsumowanieNastaw({
  organizationId,
  agent,
}: {
  organizationId: string;
  agent: AgentReceptora;
}) {
  // `!= null`, nie `!== null`: wiersz sprzed migracji 733 (albo z odczytu,
  // który kolumny nie wybrał) ma pole NIEOBECNE, a nieobecne to nie komplet.
  // PUSTA lista zmian JEST kompletem — stan nazwany „bez zmiany” (D1).
  const komplet =
    agent.zlecenie_magazyn_zrodlowy != null &&
    agent.zlecenie_magazyn_docelowy != null &&
    agent.zlecenie_zmiany != null;
  const bezZmiany = komplet && (agent.zlecenie_zmiany?.length ?? 0) === 0;
  const agentZaStary =
    bezZmiany && !wersjaAgentaCoNajmniej(agent.app_version, WERSJA_ZLECEN_BEZ_ZMIANY);

  // Nazwy magazynów i zmian z ostatniego odczytu — bez niego zostają numery.
  // Jedno małe zapytanie na agenta z nastawami; brak odczytu nie jest błędem.
  const [slowniki, setSlowniki] = useState<SlownikiNastaw | null>(null);
  useEffect(() => {
    if (!komplet) return;
    let zywe = true;
    void ostatniOdczytNastaw(organizationId, agent.id)
      .then((z) => {
        // Nazwy WYŁĄCZNIE z odczytu obecnej bazy — słownik poprzedniej
        // podpisałby numery cudzymi nazwami magazynów.
        if (zywe)
          setSlowniki(z && odczytNastawAktualny(z, agent) ? slownikiZWyniku(z.wynik) : null);
      })
      .catch(() => undefined);
    return () => {
      zywe = false;
    };
    // `agent` jako całość zmienia się co tik listy — zależności to pola, które decydują.
  }, [
    komplet,
    organizationId,
    agent.id,
    agent.updated_at,
    agent.receptor_komplet_od,
    agent.receptor_nastawy_zdjete_at,
  ]);

  return (
    <p className="mt-2 text-xs text-aurora-muted" data-testid={`nastawy-${agent.id}`}>
      {komplet ? (
        <>
          Nastawy zlecenia:{' '}
          {etykietaPozycji(agent.zlecenie_magazyn_zrodlowy as number, slowniki?.magazyny)} →{' '}
          {etykietaPozycji(agent.zlecenie_magazyn_docelowy as number, slowniki?.magazyny)} · zmiany:{' '}
          {bezZmiany
            ? 'bez przypisanej zmiany'
            : (agent.zlecenie_zmiany ?? [])
                .map((id) => etykietaPozycji(id, slowniki?.zmiany))
                .join(', ')}
          {agentZaStary && (
            <span className="block text-aurora-warning" data-testid={`agent-za-stary-${agent.id}`}>
              Zlecenia bez zmiany wymagają agenta {WERSJA_ZLECEN_BEZ_ZMIANY} lub nowszego — ten ma{' '}
              {agent.app_version ? `v${agent.app_version}` : 'nieznaną wersję'}. Zaktualizuj agenta
              na serwerze, inaczej zlecenia z Mandatum będą odrzucane.
            </span>
          )}
        </>
      ) : agent.receptor_nastawy_zdjete_at ? (
        <>
          Brak nastaw zlecenia — agent zgłosił inną bazę albo inne API Receptora niż poprzednio,
          więc magazyny i zmiany z tamtej bazy zdjęto. Odczytaj je na nowo w „Nastawy”.
        </>
      ) : (
        <>Brak nastaw zlecenia — zlecenia z Mandatum do tego agenta będą odrzucane.</>
      )}
    </p>
  );
}

/**
 * Okno nastaw: listy wyboru z OSTATNIEGO ODCZYTU magazynów i zmian tego
 * agenta. Bez odczytu nie ma list — identyfikatory są z bazy Receptora
 * klienta i człowiek nie ma ich skąd znać, więc pierwszym krokiem jest
 * przycisk „Odczytaj magazyny i zmiany", a okno czeka na wynik.
 */
function OknoNastaw({
  organizationId,
  agent,
  pollMs,
  onZamknij,
}: {
  organizationId: string;
  agent: AgentReceptora;
  pollMs: number;
  onZamknij: (zapisano: boolean) => void;
}) {
  const [slowniki, setSlowniki] = useState<SlownikiNastaw | null>(null);
  /**
   * Odczyt pominięty jako NIEAKTUALNY (sprzed zgłoszenia obecnej bazy albo
   * API) — jego data, żeby okno nazwało powód zamiast udawać, że odczytu
   * nie było.
   */
  const [odczytSprzedZmiany, setOdczytSprzedZmiany] = useState<string | null>(null);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);
  const [odczyt, setOdczyt] = useState<string | null>(null);
  const [zapisuje, setZapisuje] = useState(false);
  const [zrodlowy, setZrodlowy] = useState(agent.zlecenie_magazyn_zrodlowy?.toString() ?? '');
  const [docelowy, setDocelowy] = useState(agent.zlecenie_magazyn_docelowy?.toString() ?? '');
  const [zmiany, setZmiany] = useState<number[]>(agent.zlecenie_zmiany ?? []);

  useEffect(() => {
    let zywe = true;
    void (async () => {
      try {
        const z = await ostatniOdczytNastaw(organizationId, agent.id);
        if (!zywe) return;
        // Odczyt sprzed zmiany bazy albo API to słownik INNEJ bazy (T14) —
        // nie podsuwamy go do wyboru; okno prosi o odczyt na nowo.
        if (z && !odczytNastawAktualny(z, agent)) {
          setSlowniki(null);
          setOdczytSprzedZmiany(z.created_at);
        } else {
          setSlowniki(z ? slownikiZWyniku(z.wynik) : null);
        }
      } catch (err) {
        if (zywe) setBlad(err instanceof ReceptorCoreError ? err.message : 'Błąd odczytu');
      } finally {
        if (zywe) setLadowanie(false);
      }
    })();
    return () => {
      zywe = false;
    };
  }, [organizationId, agent.id]);

  // Czekanie na wynik zleconego odczytu — po id zadania, więc błąd agenta
  // kończy czekanie z NAZWANYM powodem, a nie wieczną kręciołką.
  useEffect(() => {
    if (!odczyt) return;
    const id = setInterval(() => {
      void (async () => {
        try {
          const z = await zadanieWgId(organizationId, odczyt);
          if (!z || zadanieWToku(z)) return;
          setOdczyt(null);
          if (z.status === 'blad') setBlad(z.blad ?? 'Agent nie odczytał magazynów i zmian');
          else {
            setOdczytSprzedZmiany(null);
            setSlowniki(slownikiZWyniku(z.wynik));
          }
        } catch {
          // Chwilowy błąd odczytu — następne odpytanie spróbuje ponownie.
        }
      })();
    }, pollMs);
    return () => clearInterval(id);
  }, [odczyt, organizationId, pollMs]);

  const odczytaj = async () => {
    setBlad(null);
    try {
      const z = await zlecZadanie(organizationId, {
        rodzaj: RODZAJ_ODCZYTU_NASTAW,
        agent_id: agent.id,
      });
      setOdczyt(z.id);
    } catch (err) {
      setBlad(err instanceof ReceptorCoreError ? err.message : 'Nie udało się zlecić odczytu');
    }
  };

  const zapisz = async (wyczysc: boolean) => {
    setZapisuje(true);
    setBlad(null);
    try {
      await zapiszNastawy(
        organizationId,
        agent.id,
        wyczysc
          ? null
          : {
              magazyn_zrodlowy: Number(zrodlowy),
              magazyn_docelowy: Number(docelowy),
              zmiany: [...zmiany].sort((a, b) => a - b),
            }
      );
      onZamknij(true);
    } catch (err) {
      setBlad(err instanceof ReceptorCoreError ? err.message : 'Nie udało się zapisać nastaw');
      setZapisuje(false);
    }
  };

  // Zapisane identyfikatory, których nie ma w ostatnim odczycie z Receptora.
  // Mogą to być zmiana lub magazyn usunięte w Managerze. Wysłane jeszcze raz
  // dałyby odmowę KAŻDEGO zlecenia („Some workshifts were not found"), więc
  // pokazujemy je osobno, dajemy je odznaczyć i nie pozwalamy ich zapisać.
  const zmianyNieaktualne = slowniki
    ? zmiany.filter((id) => !slowniki.zmiany.some((z) => z.id === id))
    : [];
  const magazynNieaktualny = (wartosc: string) =>
    slowniki !== null && wartosc !== '' && !slowniki.magazyny.some((m) => String(m.id) === wartosc);
  const magazynyNieaktualne = [zrodlowy, docelowy].filter(magazynNieaktualny);
  // Pusta lista zmian JEST kompletem — „bez zmiany” (D1, Receptor przyjmuje
  // `WorkshiftIds: []`). U OGYY `dbo.WorkShift` jest pusta.
  // Zapis wymaga list z odczytu OBECNEJ bazy: bez nich wybór pochodziłby
  // z pamięci okna albo z bazy, której agent już nie obsługuje.
  const kompletne =
    slowniki !== null &&
    zrodlowy !== '' &&
    docelowy !== '' &&
    zmianyNieaktualne.length === 0 &&
    magazynyNieaktualne.length === 0;
  const magazyny = (slowniki?.magazyny ?? []).map((m) => ({
    value: String(m.id),
    label: etykietaPozycji(m.id, slowniki?.magazyny),
  }));
  const maNastawy = agent.zlecenie_magazyn_zrodlowy != null;

  return (
    <Modal
      open
      onClose={() => onZamknij(false)}
      title={`Nastawy zlecenia — ${agent.name}`}
      description="Na które magazyny i zmiany Receptor ma zakładać zlecenia produkcyjne wysłane z Mandatum."
      footer={
        <ModalFooter>
          {maNastawy && (
            <Button variant="ghost" onClick={() => void zapisz(true)} disabled={zapisuje}>
              Wyczyść nastawy
            </Button>
          )}
          <Button variant="ghost" onClick={() => onZamknij(false)} disabled={zapisuje}>
            Anuluj
          </Button>
          <Button onClick={() => void zapisz(false)} disabled={!kompletne || zapisuje}>
            {zapisuje ? <Spinner size="sm" /> : <Settings2 className="w-4 h-4" />}
            Zapisz
          </Button>
        </ModalFooter>
      }
    >
      {blad && (
        <Alert variant="error" className="mb-4">
          {blad}
        </Alert>
      )}

      {ladowanie ? (
        <div className="space-y-2">
          <div className="skel h-10" />
          <div className="skel h-10" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-aurora-muted">
              {slowniki
                ? `Magazyny i zmiany odczytane z bazy Receptora${
                    slowniki.pobrano_at
                      ? ` ${new Date(slowniki.pobrano_at).toLocaleString('pl-PL')}`
                      : ''
                  }.`
                : odczytSprzedZmiany
                  ? `Ostatni odczyt magazynów i zmian (${new Date(odczytSprzedZmiany).toLocaleString('pl-PL')}) pochodzi sprzed zmiany bazy albo API Receptora zgłoszonej przez agenta — to lista innej bazy. Odczytaj je na nowo.`
                  : 'Najpierw odczytaj magazyny i zmiany z bazy Receptora — agent poda listę.'}
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void odczytaj()}
              disabled={odczyt !== null || agent.status !== 'active'}
              title={
                agent.status === 'active'
                  ? undefined
                  : 'Agent musi być sparowany, żeby odczytać bazę Receptora'
              }
            >
              {odczyt ? <Spinner size="sm" /> : <Database className="w-4 h-4" />}
              {odczyt ? 'Agent czyta bazę…' : 'Odczytaj magazyny i zmiany'}
            </Button>
          </div>

          {slowniki && (zmianyNieaktualne.length > 0 || magazynyNieaktualne.length > 0) && (
            <Alert variant="warning">
              Część zapisanych nastaw nie istnieje już w Receptorze
              {magazynyNieaktualne.length > 0
                ? ` (magazyn ${[...new Set(magazynyNieaktualne)].map((m) => `#${m}`).join(', ')})`
                : ''}
              {zmianyNieaktualne.length > 0
                ? ` (zmiana ${zmianyNieaktualne.map((z) => `#${z}`).join(', ')})`
                : ''}
              . Wybierz aktualne pozycje i zapisz — do tego czasu Receptor odrzuci każde zlecenie.
            </Alert>
          )}

          {slowniki && (
            <>
              <Select
                label="Magazyn źródłowy (surowce)"
                placeholder="Wybierz magazyn"
                options={magazyny}
                value={zrodlowy}
                onChange={(e) => setZrodlowy(e.target.value)}
              />
              <Select
                label="Magazyn docelowy (wyroby)"
                placeholder="Wybierz magazyn"
                options={magazyny}
                value={docelowy}
                onChange={(e) => setDocelowy(e.target.value)}
              />
              <fieldset>
                <legend className="block text-sm font-medium text-aurora-text mb-1.5">
                  Zmiany
                </legend>
                {slowniki.zmiany.length === 0 ? (
                  <p className="text-xs text-aurora-muted">
                    Receptor nie ma zdefiniowanych zmian — zlecenie powstanie bez przypisanej zmiany
                    i jest to dozwolone. Zmianę dodaje się w Managerze:{' '}
                    <strong>USTAWIENIA › ZMIANY</strong>, ikona <strong>Utwórz</strong> (uprawnienie{' '}
                    <code>WorkShifts</code>).
                  </p>
                ) : (
                  <div className="space-y-1">
                    {slowniki.zmiany.map((z) => (
                      <label
                        key={z.id}
                        className="flex items-center gap-2 text-sm text-aurora-text"
                      >
                        <input
                          type="checkbox"
                          checked={zmiany.includes(z.id)}
                          onChange={(e) =>
                            setZmiany((obecne) =>
                              e.target.checked
                                ? [...obecne, z.id]
                                : obecne.filter((x) => x !== z.id)
                            )
                          }
                        />
                        {etykietaPozycji(z.id, slowniki.zmiany)}
                      </label>
                    ))}
                  </div>
                )}
                {slowniki.zmiany.length > 0 && zmiany.length === 0 && (
                  <p className="text-xs text-aurora-muted mt-1">
                    Nie zaznaczono żadnej zmiany — zlecenie powstanie bez przypisanej zmiany i jest
                    to dozwolone.
                  </p>
                )}
                {zmiany.length === 0 &&
                  !wersjaAgentaCoNajmniej(agent.app_version, WERSJA_ZLECEN_BEZ_ZMIANY) && (
                    <Alert variant="warning" className="mt-2">
                      Zlecenia bez zmiany wymagają agenta {WERSJA_ZLECEN_BEZ_ZMIANY} lub nowszego —
                      ten agent ma {agent.app_version ? `v${agent.app_version}` : 'nieznaną wersję'}
                      . Zaktualizuj agenta na serwerze; do tego czasu zlecenia z Mandatum będą
                      odrzucane.
                    </Alert>
                  )}
                {zmianyNieaktualne.map((id) => (
                  <label key={id} className="flex items-center gap-2 text-sm text-aurora-text">
                    <input
                      type="checkbox"
                      checked
                      onChange={() => setZmiany((obecne) => obecne.filter((x) => x !== id))}
                    />
                    Zmiana #{id} — nie ma jej już w Receptorze, odznacz
                  </label>
                ))}
              </fieldset>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Pasek stanu licencji agenta — między nagłówkiem a alertami. */
export function PasekLicencji({
  licencja,
  licencjaOperatora = null,
}: {
  licencja: StanLicencjiReceptora;
  /** Adres ekranu licencji super-admina — `null` dla każdego innego. */
  licencjaOperatora?: string | null;
}) {
  const { ton, tekst } = zdanieLicencji(licencja);
  return (
    <Alert variant={ton === 'ok' ? 'info' : ton === 'uwaga' ? 'warning' : 'error'} className="mb-4">
      {tekst}
      {licencjaOperatora && ton !== 'ok' && (
        <>
          {' '}
          <a href={licencjaOperatora} className="underline font-medium">
            Ustaw licencję (super-admin)
          </a>
        </>
      )}
    </Alert>
  );
}
